import { useEffect, useState } from 'react';
import axios from 'axios';
import PayrollLayout from '../../components/layout/PayrollLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { FileSpreadsheet, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// PAYROLL — Reports: monthly payable-hours summary per employee using the
// CENTRAL worked-hours calculation (net after automatic break deduction).
export default function PayrollReportsPage() {
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/clock/active-employees`, { params: { month: `${year}-${month}` } })
      .then((r) => setRows((r.data?.employees || []).filter((e) => e.shift_count > 0 || e.worked_hours > 0)))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load report'))
      .finally(() => setLoading(false));
  }, [year, month]);

  const totals = rows.reduce((a, e) => ({
    shifts: a.shifts + (e.shift_count || 0),
    sched: a.sched + (e.scheduled_hours || 0),
    gross: a.gross + (e.gross_hours || 0),
    brk: a.brk + (e.break_minutes || 0),
    net: a.net + (e.worked_hours || 0),
  }), { shifts: 0, sched: 0, gross: 0, brk: 0, net: 0 });

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Payroll Report');
      ws.columns = [{ width: 32 }, { width: 10 }, { width: 16 }, { width: 13 }, { width: 12 }, { width: 18 }];
      ws.mergeCells('A1:F1');
      ws.getCell('A1').value = `SAN Security Ltd — Payroll Report — ${MONTHS[Number(month) - 1]} ${year}`;
      ws.getCell('A1').font = { size: 14, bold: true };
      ws.getCell('A1').alignment = { horizontal: 'center' };
      ws.addRow([]);
      const h = ws.addRow(['Employee', 'Shifts', 'Scheduled Hours', 'Gross Hours', 'Break (min)', 'Net Worked Hours']);
      h.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      h.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF18181B' } };
      rows.forEach((e) => {
        ws.addRow([e.name, e.shift_count, (e.scheduled_hours || 0).toFixed(2),
          (e.gross_hours || 0).toFixed(2), e.break_minutes || 0, (e.worked_hours || 0).toFixed(2)]);
      });
      ws.addRow([]);
      const t = ws.addRow(['TOTAL', totals.shifts, totals.sched.toFixed(2), totals.gross.toFixed(2), totals.brk, totals.net.toFixed(2)]);
      t.font = { bold: true };
      t.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFACC15' } }; });
      const note = ws.addRow(['Net Worked Hours = central calculation (automatic break deduction / authorised overrides applied).']);
      note.font = { italic: true, size: 9, color: { argb: 'FF6B7280' } };
      const buffer = await wb.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `Payroll_Report_${year}-${month}.xlsx`);
      if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        toast.success('Excel downloaded', { description: "Saved to Downloads — open the download notification to view it.", duration: 10000 });
      } else { toast.success('Excel downloaded'); }
    } catch { toast.error('Failed to generate Excel'); }
    finally { setDownloading(false); }
  };

  return (
    <PayrollLayout>
      <div className="space-y-4" data-testid="payroll-reports-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-barlow font-bold tracking-wider uppercase flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" strokeWidth={1.5} /> Payroll Reports
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Payable hours per employee — net of automatic break deductions (central calculation).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-36 bg-card border-border rounded-sm" data-testid="report-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={m} value={String(i + 1).padStart(2, '0')}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-24 bg-card border-border rounded-sm" data-testid="report-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0, 1, 2].map((d) => {
                  const y = String(now.getFullYear() - d);
                  return <SelectItem key={y} value={y}>{y}</SelectItem>;
                })}
              </SelectContent>
            </Select>
            <Button
              onClick={downloadExcel} disabled={downloading || rows.length === 0}
              variant="outline"
              className="border-green-600 text-green-500 hover:bg-green-900/20 rounded-sm"
              data-testid="download-payroll-report-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1.5" /> {downloading ? 'Preparing…' : 'Download Excel'}
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground animate-pulse">Loading report…</p>
        ) : rows.length === 0 ? (
          <Card className="bg-card border-border rounded-sm"><CardContent className="py-10 text-center text-sm text-muted-foreground">No worked data for this month.</CardContent></Card>
        ) : (
          <Card className="bg-card border-border rounded-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="payroll-report-table">
              <thead>
                <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-3 py-2">Employee</th>
                  <th className="text-right px-3 py-2">Shifts</th>
                  <th className="text-right px-3 py-2">Scheduled</th>
                  <th className="text-right px-3 py-2">Gross</th>
                  <th className="text-right px-3 py-2">Break</th>
                  <th className="text-right px-3 py-2 text-primary">Net Worked</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id} className="border-b border-border/40 hover:bg-accent/30" data-testid={`report-row-${e.id}`}>
                    <td className="px-3 py-2">{e.name}</td>
                    <td className="px-3 py-2 text-right font-mono">{e.shift_count}</td>
                    <td className="px-3 py-2 text-right font-mono">{(e.scheduled_hours || 0).toFixed(2)}h</td>
                    <td className="px-3 py-2 text-right font-mono">{(e.gross_hours || 0).toFixed(2)}h</td>
                    <td className="px-3 py-2 text-right font-mono">{e.break_minutes || 0}m</td>
                    <td className="px-3 py-2 text-right font-mono text-primary font-semibold">{(e.worked_hours || 0).toFixed(2)}h</td>
                  </tr>
                ))}
                <tr className="font-bold bg-muted/30">
                  <td className="px-3 py-2">TOTAL</td>
                  <td className="px-3 py-2 text-right font-mono">{totals.shifts}</td>
                  <td className="px-3 py-2 text-right font-mono">{totals.sched.toFixed(2)}h</td>
                  <td className="px-3 py-2 text-right font-mono">{totals.gross.toFixed(2)}h</td>
                  <td className="px-3 py-2 text-right font-mono">{totals.brk}m</td>
                  <td className="px-3 py-2 text-right font-mono text-primary" data-testid="report-total-net">{totals.net.toFixed(2)}h</td>
                </tr>
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </PayrollLayout>
  );
}
